/**
 * El NumberInput respeta los parámetros de ambiente:
 * - AMOUNT_DECIMAL_SEPARATOR
 * - AMOUNT_DECIMAL_ZEROS
 * - CHAR_DECIMAL_SEPARATOR
 * - CHAR_THOUS_SEPARATOR
 * - ADD_THOUSAND_SEPARATOR
 */

import React from 'react';
import { Input, InputProps } from '@apia/theme';
import { ApiaUtil } from '@apia/components';
import { getLabel } from '../../../utils/util';

export type TNumberInputChangeEvent = { error: boolean; value: string };

export type TNumberInput = Omit<
  InputProps,
  'type' | 'onChange' | 'onBlur' | 'value' | 'defaultValue'
> & {
  onChange?: (ev: TNumberInputChangeEvent) => unknown;
  value?: string;
  defaultValue?: string;
  avoidDecimalRestriction?: boolean;
  showAsInteger?: boolean;
};

function fillZeros(amount: number) {
  return Array(Math.max(0, amount)).fill(0).join('');
}

function fillThousandsZeros(value: number) {
  const stringValue = String(value);
  return `${fillZeros(3 - stringValue.length)}${String(value)}`;
}

const decimalCount = Number(window.AMOUNT_DECIMAL_SEPARATOR);
const decimalZeros = Number(window.AMOUNT_DECIMAL_ZEROS);
const decimalSeparator = window.CHAR_DECIMAL_SEPARATOR;
const thousandsSeparator = window.CHAR_THOUS_SEPARATOR;
const regexpThousandsSeparator =
  thousandsSeparator === '.' ? '\\.' : thousandsSeparator;
const regexpDecimalSeparator =
  decimalSeparator === '.' ? '\\.' : decimalSeparator;
const shouldRound = false;

export function parseNumberInputValueToNumber(value: string): number {
  return Number.parseFloat(
    value.replaceAll(thousandsSeparator, '').replaceAll(decimalSeparator, '.'),
  );
}

export function parseNumberValueToNumberInput(value: string | number): string {
  const stringNumber = String(value);
  const result = stringNumber.match(/(-?)(\d+)(?:\.(\d+))/);
  if (!result) return '';

  const isNegative = !!result[1];
  const integer = result[2];
  const decimals = result[3] as string | null;
  const decimalsString = decimals ? `${regexpDecimalSeparator}${decimals}` : '';

  return (
    formatValueToShow(`${isNegative ? '-' : ''}${integer}${decimalsString}`) ||
    ''
  );
}

function formatValueToShow(
  value: string,
  avoidDecimalRestriction = false,
  forceWholeNumber = false,
): string | null {
  const result = value.match(
    new RegExp(
      `^(-)?((?:(?:\\d{1,3})${regexpThousandsSeparator}(?:\\d{3}${regexpThousandsSeparator})*(?:\\d{3}))?|(?:\\d*))(?:(?:${regexpDecimalSeparator})(\\d+))?$`,
    ),
  );
  if (!result) return null;

  const isNegative = !!result[1];
  let module = Number(result[2].replaceAll(thousandsSeparator, ''));
  const slicedDecimals = avoidDecimalRestriction
    ? result[3]
    : (result[3]?.slice(0, decimalCount + 1) ?? '');

  let decimalsString = '';

  if (!forceWholeNumber) {
    if (!avoidDecimalRestriction) {
      const noZeroDecimals =
        (slicedDecimals
          ? `${String(
              Math.round(
                Number(
                  `${slicedDecimals}${fillZeros(
                    (shouldRound ? decimalCount + 1 : decimalCount) -
                      slicedDecimals.length,
                  )}`,
                ) / (shouldRound ? 10 : 1),
              ),
            )}`
          : ''
        )
          .slice(0, decimalCount)
          .match(/(\d*[1-9])0*/)?.[1] ?? '';
      decimalsString = `${noZeroDecimals}${fillZeros(
        decimalZeros - noZeroDecimals.length,
      )}`;
    } else {
      decimalsString = slicedDecimals;
    }
  }

  const finalNumberString: string[] = [];
  while (module >= 1000) {
    module /= 1000;
    const truncedInteger = Math.trunc(module);
    finalNumberString.unshift(
      fillThousandsZeros(Math.round((module - truncedInteger) * 1000)),
    );
    module = truncedInteger;
  }
  finalNumberString.unshift(String(module));

  let finalResult = `${isNegative ? '-' : ''}${finalNumberString.join(
    window.ADD_THOUSAND_SEPARATOR && !forceWholeNumber
      ? window.CHAR_THOUS_SEPARATOR
      : '',
  )}${decimalsString.length > 0 ? `${decimalSeparator}${decimalsString}` : ''}`;

  if (forceWholeNumber) {
    finalResult = finalResult.split(decimalSeparator)[0];
  }

  return finalResult;
}
function formatDecimalValue(input: string): string {
  let normalized = input;

  if (normalized.endsWith(decimalSeparator)) {
    normalized += '0';
  }
  let num = ApiaUtil.instance.parsers.jsNumber(normalized);

  const factor = Math.pow(10, decimalCount);
  num = Math.round(num * factor) / factor;

  // eslint-disable-next-line prefer-const
  let [intPart, decPart = ''] = num.toString().split(decimalSeparator);

  if (decimalCount === 0) return intPart;

  decPart = decPart.slice(0, decimalCount).padEnd(decimalZeros, '0');

  return `${intPart},${decPart}`;
}
const NumberInput = React.forwardRef<HTMLInputElement, TNumberInput>(
  ({ value, avoidDecimalRestriction, showAsInteger, ...props }, ref) => {
    const [actualValue, setActualValue] = React.useState<string>('');
    const [showValue, setShowValue] = React.useState<string>('');
    const lastEmittedValue = React.useRef<string>('');

    const setValue = React.useCallback(
      (newValue: string, isBlur?: boolean, emitChange = true) => {
        if (newValue === '') {
          if (newValue === lastEmittedValue.current) return;
          setActualValue('');
          setShowValue('');
          if (emitChange) props.onChange?.({ value: '', error: false });
        } else {
          const parsedValue = formatValueToShow(
            newValue.endsWith(decimalSeparator)
              ? formatDecimalValue(newValue)
              : newValue,
            avoidDecimalRestriction,
            showAsInteger,
          );
          if (parsedValue === null) {
            setActualValue('');
            setShowValue('');
            if (emitChange) {
              props.onChange?.({ value: '', error: true });
              ApiaUtil.instance.notifications.notify({
                message: getLabel('msgMustBeNum').text,
              });
            }
          } else {
            if (
              newValue === parsedValue &&
              newValue === lastEmittedValue.current
            )
              return;
            setActualValue(isBlur ? parsedValue : newValue);
            setShowValue(isBlur ? parsedValue : newValue);
            if (emitChange) {
              props.onChange?.({
                value: isBlur ? parsedValue : newValue,
                error: false,
              });
            }
          }
        }
        lastEmittedValue.current = newValue;
      },
      [avoidDecimalRestriction, showAsInteger, props],
    );

    React.useEffect(() => {
      if (value !== actualValue) {
        setValue((value as string) ?? '', undefined, false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <Input
        {...props}
        onFocus={React.useCallback(
          (ev: React.FocusEvent<HTMLInputElement>) => {
            props.onFocus?.(ev);
            setShowValue(
              String(actualValue).replaceAll(thousandsSeparator, ''),
            );
          },
          [actualValue, props],
        )}
        onBlur={React.useCallback(
          (ev: React.FocusEvent<HTMLInputElement>) => {
            setValue(ev.target.value, true);
          },
          [setValue],
        )}
        onChange={React.useCallback(
          (ev: React.ChangeEvent<HTMLInputElement>) => {
            if (
              actualValue !== ev.target.value &&
              !ev.target.value.includes(decimalSeparator)
            ) {
              setValue(ev.target.value);
            } else {
              setShowValue(ev.target.value);
            }
          },
          [actualValue, setValue],
        )}
        type="text"
        ref={ref}
        value={showValue}
      />
    );
  },
);

export default NumberInput;
